import { FC, useEffect, useState, useRef, useContext } from 'react';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import { ITheme } from '@/src/lib/types/themeInterface';
import styles from './MainInfo.module.scss';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import imageHelper from '@/src/lib/helpers/imageHelper';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import { useInView } from 'react-intersection-observer';
import { HeaderContext } from '@/src/components/providers/headerContextProvider/HeaderContextProvider';
import ShopifyStoreButton from '@/src/components/ui/shopifyButton/ShopifyStoreButton';

type TMain = {
  themeData: Partial<ITheme>;
};

const MainInfo: FC<TMain> = ({ themeData }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  const sectionRef = useRef<HTMLElement>(null);
  const [isFullyScrolled, setIsFullyScrolled] = useState(false);
  const { setIsSectionScrolled } = useContext(HeaderContext);

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionRef.current;
      if (section) {
        const { bottom } = section.getBoundingClientRect();
        const isSectionFullyVisible = bottom >= 0;
        setIsFullyScrolled(!isSectionFullyVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    isFullyScrolled ? setIsSectionScrolled(true) : setIsSectionScrolled(false);
  }, [isFullyScrolled]);

  return (
    <>
      <section
          className={`${styles.section__container} ${
              themeData.slug === 'stockist'
                  ? styles.section__container_stockist
              : themeData.slug === 'prompt'
                  ? styles.section__container_prompt
                  : ''
          }`}
        ref={sectionRef}>
        <Wrapper className={styles.section__wrapper}>
          <div className={styles.section__left}>
            <div ref={ref} className={styles.section__info}>
              <h1 className={`heading__1 ${styles.heading} ${inView && styles.animate__heading}`}>
                {themeData.title &&
                    createAnimationArr(themeData.title, styles.word__wrapper, 0, 0.01)}
              </h1>
              <p className={`excerpt ${styles.excerpt} ${inView && styles.animate__excerpt}`}>
                {themeData.excerpt &&
                    createAnimationArr(themeData.excerpt, styles.word__wrapper, 0.3, 0.01)}
              </p>
              <div className={styles.buttons}>
                <ShopifyStoreButton
                    isWhite={themeData.slug !== 'prompt'}
                    theme={themeData as ITheme}
                />
              </div>
            </div>
          </div>
          <div className={styles.theme__right}>
            {themeData.themeMobile && (
                <div className={styles.theme__mobile}>
                  <NextImage
                      src={imageHelper(themeData.themeMobile).url}
                      alt={imageHelper(themeData.themeMobile).alt ?? 'Theme mobile'}
                      fill
                      priority
                      sizes="(max-width: 376px) 26.8vw, (max-width: 480px) 24.75vw, (max-width: 576px) 23.4vw, (max-width: 1024px) 18.75vw, (max-width: 1600px) 13.19vw, (max-width: 1920px) 14.29vw, 273px"
                  />
                </div>
            )}
            {themeData.themeDesktop && (
                <div className={styles.theme__desktop}>
                  <NextImage
                      src={imageHelper(themeData.themeDesktop).url}
                      alt={imageHelper(themeData.themeDesktop).alt ?? 'Theme desktop'}
                      fill
                      priority
                      sizes="
                            (max-width: 376px) 72.34vw,
                            (max-width: 576px) 73.33vw,
                            (max-width: 1024px) 70.31vw,
                            (max-width: 1600px) 49.58vw,
                            (max-width: 1920px) 50.3vw,
                                                961.11px"
                  />
                </div>
            )}
          </div>
        </Wrapper>
      </section>
    </>
  );
};

export default MainInfo;
